/**
 * App Initializer Component
 * 
 * Handles dynamic loading of external scripts based on user consent preferences.
 * This component loads analytics and marketing scripts only when consent is granted
 * with performance optimizations and proper error handling.
 * 
 * PERFORMANCE OPTIMIZATION: Analytics loading is deferred until user interaction
 * or 5 seconds to improve LCP and reduce initial JavaScript burden.
 */
import React, { useEffect, useCallback, useState } from 'react';

import { useCookieConsent } from '../../hooks/useCookieConsent';
import { setupDeferredFirebaseInit } from '../../utils/firebaseLoader';
import { 
  loadFacebookPixel, 
  preloadScript 
} from '../../utils/loadScript';
import { logger } from '../../utils/logger';

// Configuration for external scripts
const SCRIPT_CONFIG = {
  // Facebook Pixel ID - replace with your actual ID
  FB_PIXEL_ID: process.env.REACT_APP_FB_PIXEL_ID,
};

// Cache to prevent duplicate script loading
const loadedScripts = new Set<string>();

/**
 * App Initializer Component
 * 
 * Monitors consent changes and loads/unloads scripts accordingly
 * Note: Firebase Analytics is initialized based on analytics consent
 */
const AppInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { consent } = useCookieConsent();
  
  // CRITICAL PERFORMANCE FIX: Add user interaction tracking for marketing scripts
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  
  // Track Firebase analytics initialization
  const [firebaseCleanup, setFirebaseCleanup] = useState<(() => void) | null>(null);

  const loadMarketingScripts = useCallback(async () => {
    if (!consent.marketing) return;

    // Load Facebook Pixel
    if (SCRIPT_CONFIG.FB_PIXEL_ID && 
        !loadedScripts.has('fbpixel')) {
      try {
        await loadFacebookPixel(SCRIPT_CONFIG.FB_PIXEL_ID);
        loadedScripts.add('fbpixel');
        logger.info('Facebook Pixel loaded successfully');
      } catch (error) {
        logger.error('Failed to load Facebook Pixel', { error });
      }
    }
  }, [consent.marketing]);

  // User interaction tracking for marketing scripts
  useEffect(() => {
    if (!hasUserInteracted) {
      const handleUserInteraction = () => {
        setHasUserInteracted(true);
        
        // Remove listeners after first interaction
        window.removeEventListener('scroll', handleUserInteraction);
        window.removeEventListener('mousemove', handleUserInteraction);
        window.removeEventListener('touchstart', handleUserInteraction);
        window.removeEventListener('click', handleUserInteraction);
      };

      // Wait for user interaction
      window.addEventListener('scroll', handleUserInteraction, { passive: true, once: true });
      window.addEventListener('mousemove', handleUserInteraction, { passive: true, once: true });
      window.addEventListener('touchstart', handleUserInteraction, { passive: true, once: true });
      window.addEventListener('click', handleUserInteraction, { passive: true, once: true });

      return () => {
        window.removeEventListener('scroll', handleUserInteraction);
        window.removeEventListener('mousemove', handleUserInteraction);
        window.removeEventListener('touchstart', handleUserInteraction);
        window.removeEventListener('click', handleUserInteraction);
      };
    }
  }, [hasUserInteracted]);

  // Preload scripts when consent is likely (on first interaction)
  const preloadScripts = useCallback(() => {
    // Only preload if we have valid configuration
    if (SCRIPT_CONFIG.FB_PIXEL_ID) {
      preloadScript('https://connect.facebook.net/en_US/fbevents.js');
    }
  }, []);

  // Load marketing scripts when consent is granted
  useEffect(() => {
    if (consent.marketing) {
      loadMarketingScripts().catch(error => {
        console.error('Error loading marketing scripts:', error);
      });
    }
  }, [consent.marketing, loadMarketingScripts]);

  // Preload scripts on user interaction for better performance
  useEffect(() => {
    let hasInteracted = false;

    const handleInteraction = () => {
      if (!hasInteracted) {
        hasInteracted = true;
        preloadScripts();
        
        // Remove event listeners after first interaction
        document.removeEventListener('click', handleInteraction);
        document.removeEventListener('touchstart', handleInteraction);
        document.removeEventListener('keydown', handleInteraction);
      }
    };

    // Add interaction listeners
    document.addEventListener('click', handleInteraction, { passive: true });
    document.addEventListener('touchstart', handleInteraction, { passive: true });
    document.addEventListener('keydown', handleInteraction, { passive: true });

    // Cleanup
    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, [preloadScripts]);

  // Initialize Firebase Analytics when analytics consent is granted
  useEffect(() => {
    if (consent.analytics) {
      // Clean up any existing initialization
      if (firebaseCleanup) {
        firebaseCleanup();
      }
      
      // Set up deferred Firebase initialization with analytics consent
      const cleanup = setupDeferredFirebaseInit({
        analyticsDelay: 2000, // 2 second delay for performance
        onUserInteraction: true
      });
      
      setFirebaseCleanup(() => cleanup);
      
      logger.info('Firebase Analytics initialization enabled');
    } else {
      // Clean up Firebase initialization if consent is revoked
      if (firebaseCleanup) {
        firebaseCleanup();
        setFirebaseCleanup(null);
      }
      
      logger.info('Firebase Analytics disabled due to consent');
    }
    // NOTE: firebaseCleanup is intentionally excluded from dependencies to prevent infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent.analytics]);

  // Cleanup Firebase on component unmount
  useEffect(() => {
    return () => {
      if (firebaseCleanup) {
        firebaseCleanup();
      }
    };
  }, [firebaseCleanup]);

  // Note: Analytics is now handled by Firebase Analytics
  // Marketing script removal when consent is revoked is complex and may require page reload
  // For now, we only load scripts when consent is granted

  return <>{children}</>;
};

export default AppInitializer; 